// WebSocketInitializer.tsx
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { webSocketService } from './WebSocketService';
import { FeedActionContext, useFeedActions } from './FeedState';
import { useSubscribedSymbolsContext } from './context/DataFeedSymbolsContext';
import { useDispatcher } from '../global/hooks';
import { useFeedStatus, useWebSocketCloseReason } from './FeedSelectors';
import { useStateSelector } from '../global/dataSelectors';
import { createSelector } from 'reselect';
import { DataState } from '../global/state';
import { Websockets } from '../../constants';
import { useFeatureFlag } from '@tradingblock/components';
import _ from 'lodash';

const canConnectToWebsocket = createSelector(
  (s: DataState) => s.auth,
  auth => (auth.isExpired !== true && auth.isAuthenticated === true ? true : false)
);

const WebSocketInitializer: React.FC = () => {
  const dispatch = useContext(FeedActionContext);
  const { setSubscribedQuotes, setSubscribedSpreads } = useSubscribedSymbolsContext();
  const [shouldReconnect, setShouldReconnect] = useState(false);
  const retryTimeoutMs = useRef<number>(250);
  const retryTimeout = useRef<NodeJS.Timeout>();
  const dispatchers = useDispatcher();
  const dispatcher = dispatchers.dispatcher.feed;
  const globalDispatch = dispatchers.dispatch;
  const fullDispatcher = dispatchers.dispatcher;
  const feedStatus = useFeedStatus();
  const closeReason = useWebSocketCloseReason();
  const canConnect = useStateSelector(canConnectToWebsocket);

  // Check if old WebSocket is needed based on feature flags
  const { isEnabled: useNewQuoteService } = useFeatureFlag('new_quote_service');
  const { isEnabled: useNewSpreadService } = useFeatureFlag('new_spread_service');
  const { isEnabled: useLofiOrderUpdates } = useFeatureFlag('lofi-order-updates');
  const { isEnabled: useLofiCashiering } = useFeatureFlag('lofi-cashiering');
  const { isEnabled: useLofiExpiringTokens } = useFeatureFlag('lofi-expiring-tokens');

  // Old WebSocket is needed if:
  // - New quote service is disabled OR
  // - New spread service is disabled OR
  // - Order updates use old WebSocket (lofi-order-updates is false) OR
  // - Cashiering uses old WebSocket (lofi-cashiering is false) OR
  // - Expired tokens use old WebSocket (lofi-expiring-tokens is false)
  // NOTE: Even if both new services are enabled, we keep old websocket available for fallback
  // but only actively use it if needed for other services or as fallback
  const isOldWebSocketNeeded = !useNewQuoteService || !useNewSpreadService || !useLofiOrderUpdates || !useLofiCashiering || !useLofiExpiringTokens;
  
  // Keep old websocket available for fallback even when both new services are enabled
  const shouldKeepOldWebSocketForFallback = useNewQuoteService && useNewSpreadService;

  const onClose = useCallback(() => {
    setSubscribedQuotes([]);
    setSubscribedSpreads([]);
  }, [setSubscribedQuotes, setSubscribedSpreads]);

  useEffect(() => {
    return () => {
      // Clean up WebSocket on unmount if needed
      onClose();
      webSocketService.closeConnection();
    };
  }, []);

  useEffect(() => {
    if (shouldReconnect) {
      if (retryTimeout.current) {
        clearTimeout(retryTimeout.current);
      }
      retryTimeout.current = setTimeout(() => {
        if (webSocketService.shouldTryReconnect() && isOldWebSocketNeeded) {
          onClose();
          webSocketService.connect(dispatch, dispatcher, globalDispatch, fullDispatcher).then(() => setShouldReconnect(false));
        }
      }, retryTimeoutMs.current);
    }
  }, [shouldReconnect, isOldWebSocketNeeded, dispatch, dispatcher, globalDispatch, onClose]);

  useEffect(() => {
    if (feedStatus === 'closed') {
      // Only show "lost connection" message if old WebSocket is actually being used
      if (!isOldWebSocketNeeded) {
        // Old WebSocket is not needed, so don't show connection lost message
        return;
      }

      if (canConnect) {
        const newTimeout = Math.min(Websockets.ReconnectInterval, retryTimeoutMs.current + retryTimeoutMs.current);
        const newTimeoutDisplay = _.round(newTimeout / 1000, 2);
        const closeMessage = `Socket is closed. Reconnect will be attempted in ${newTimeoutDisplay} second.`;
        dispatcher.closed({
          reason: closeReason || '',
          retryingInSeconds: newTimeoutDisplay,
          message: closeMessage,
        });
        retryTimeoutMs.current = newTimeout;
        console.warn(closeMessage);
        setShouldReconnect(true);
      } else {
        const closeMessage = 'Socket is closed because user can no longer connect';
        dispatcher.closed({ reason: closeReason || '', message: closeMessage });
        console.warn(closeMessage);
      }
    }
  }, [feedStatus, closeReason, canConnect, isOldWebSocketNeeded, dispatcher]);

  useEffect(() => {
    // If old websocket is not needed for active use, but we need it for fallback,
    // keep it initialized but don't actively manage it (let fallback handlers manage it)
    if (!isOldWebSocketNeeded && !shouldKeepOldWebSocketForFallback) {
      // Old WebSocket is not needed at all, close it
      onClose();
      webSocketService.closeConnection();
      return;
    }

    // If old websocket is needed OR we need it for fallback, initialize it
    if (canConnect && (isOldWebSocketNeeded || shouldKeepOldWebSocketForFallback)) {
      // Only connect if not already connected
      if (webSocketService.shouldTryReconnect()) {
        webSocketService.connect(dispatch, dispatcher, globalDispatch, fullDispatcher);
      }
    } else if (!canConnect && !webSocketService.shouldTryReconnect()) {
      // Only close if not needed for fallback
      if (!shouldKeepOldWebSocketForFallback) {
        onClose();
        webSocketService.closeConnection();
      }
    }
  }, [dispatch, dispatcher, globalDispatch, canConnect, isOldWebSocketNeeded, shouldKeepOldWebSocketForFallback, onClose]);

  return null; // This component does not render anything
};

export default WebSocketInitializer;
